import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import Steps from "../../components/steps/Steps";
import clint_image_icon from "../../assets/images/clint_image_icon.svg";
import {
  createEmployee,
  resetStatus,
  fetchAllDesignations,
} from "../../store/slices/userSlice";
import {
  fetchAllDepartments,
  deleteDepartment,
} from "../../store/slices/departmentSlice";
import { deleteDesignation } from "../../store/slices/designation";
import { fetchAllFormulas } from "../../store/slices/formulaSlice";
import {
  fetchCountries,
  fetchStates,
  fetchCities,
} from "../../store/slices/locationSlice";
import StatusPopup from "../../components/popups/StatusPopup";
import EditSuccessPopup from "../../components/popups/EditSuccessPopup";
import DepartmentCreatePopup from "../../components/popups/DepartmentCreatePopup";
import DesignationCreatePopup from "../../components/popups/DesignationCreatePopup";
import CountryCreatePopup from "../../components/popups/CountryCreatePopup";
import StateCreatePopup from "../../components/popups/StateCreatePopup";
import CityCreatePopup from "../../components/popups/CityCreatePopup";
import { encryptBankingData } from "../../utils/cryptoUtils";

const AddNewEmployee = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const companyId = location.state?.id || "";

  const { isSuccess, errorMessage } = useSelector((state) => state.user);
  const { departmentList } = useSelector((state) => state.department);
  const { designationList } = useSelector((state) => state.user);
  const { formulaList } = useSelector((state) => state.formula);
  const {
    countries = [],
    states = [],
    cities = [],
  } = useSelector((state) => state.location || {});

  // ─── Location popup state ───────────────────────────────────────────────────
  const [showCountryPopup, setShowCountryPopup] = useState(false);
  const [showStatePopup, setShowStatePopup] = useState(false);
  const [showCityPopup, setShowCityPopup] = useState(false);
  const [locationFormValues, setLocationFormValues] = useState({});
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [selectedStateId, setSelectedStateId] = useState("");

  // ─── Other state ────────────────────────────────────────────────────────────
  const [profileImage, setProfileImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [selectedPayType, setSelectedPayType] = useState("");
  const [selectedDepartmentId, setSelectedDepartmentId] = useState("");
  const [selectedDesignationId, setSelectedDesignationId] = useState("");

  // Delete confirm
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteField, setDeleteField] = useState("");
  const [pendingDeleteId, setPendingDeleteId] = useState(null);

  // Create popups
  const [showDepartmentCreatePopup, setShowDepartmentCreatePopup] =
    useState(false);
  const [showDesignationCreatePopup, setShowDesignationCreatePopup] =
    useState(false);

  // Submit state
  const [submiting, setSubmiting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  // ─── Location handlers ──────────────────────────────────────────────────────

  const handleOpenLocationPopup = (type, data = {}) => {
    if (type === "country") {
      setShowCountryPopup(true);
    }
    if (type === "state") {
      setSelectedCountryId(data.countryId || "");
      setShowStatePopup(true);
    }
    if (type === "city") {
      setSelectedStateId(data.stateId || "");
      setShowCityPopup(true);
    }
  };

  const handleCountryCreated = (country) => {
    dispatch(fetchCountries());
    setTimeout(() => {
      setLocationFormValues((prev) => ({
        ...prev,
        Country: String(country.id),
        CountryId: String(country.id),
        State: "",
        StateId: "",
        City: "",
        CityId: "",
      }));
    }, 300);
  };

  const handleStateCreated = (state) => {
    setLocationFormValues((prev) => ({
      ...prev,
      State: String(state.id),
      StateId: String(state.id),
      City: "",
      CityId: "",
    }));
  };

  const handleCityCreated = (city) => {
    dispatch(fetchCities(city.stateId));
    setTimeout(() => {
      setLocationFormValues((prev) => ({
        ...prev,
        City: String(city.id),
        CityId: String(city.id),
      }));
    }, 300);
  };

  // ─── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    dispatch(resetStatus());
  }, [dispatch]);

  useEffect(() => {
    if (isSuccess && isFormSubmitting) {
      setSubmiting(false);
      setShowSuccessPopup(true);
      setIsFormSubmitting(false);
    }
  }, [isSuccess, isFormSubmitting]);

  useEffect(() => {
    dispatch(fetchAllFormulas());
    dispatch(fetchAllDepartments());
    dispatch(fetchAllDesignations());
    dispatch(fetchCountries());
  }, [dispatch]);

  useEffect(() => {
    const id =
      location.state?.designationId ||
      location.state?.selectedDesignationId ||
      "";
    if (id) setSelectedDesignationId(id);
  }, [location.state]);

  useEffect(() => {
    const id =
      location.state?.departmentId ||
      location.state?.selectedDepartmentId ||
      "";
    if (id) setSelectedDepartmentId(id);
  }, [location.state]);

  useEffect(() => {
    const footer = document.querySelector("footer");
    if (footer) footer.style.display = "none";
    return () => {
      if (footer) footer.style.display = "";
    };
  }, []);

  // ─── Submit ─────────────────────────────────────────────────────────────────

  const handleFormSubmit = async (data) => {
    setSubmiting(true);
    setIsFormSubmitting(true);

    const formData = new FormData();

    formData.append("companyId", companyId);
    formData.append("code", "");
    formData.append("designationId", data["Designation"]);
    formData.append("departmentId", data["Department"]);
    formData.append("joiningDate", data["Joining Date"]);

    // Pay type
    if (data["PayType"] === "Hourly") {
      formData.append("payType", 2);
      formData.append("hourlyRate", data["Hourly Rate"]);
      formData.append("defaultRegularHours", data["Regular Hours"]);
    } else if (data["PayType"] === "Salary") {
      formData.append("payType", 1);
      formData.append("fixedSalary", data["FixedSalary"]);
    }

    // Image
    if (profileImage) {
      formData.append("file", profileImage);
    }

    // Bank details (encrypted)
    const encryptedBankingData = await encryptBankingData({
      bankHolderName: data["Holder Name"] || "",
      bankName: data["Bank Name"] || "",
      accountNumber: data["Account Number"] || "",
      routingNumber: data["Routing Number"] || "",
      description: data["Description"] || "",
    });

    formData.append(
      "SaveBankDTO.BankHolderName",
      encryptedBankingData.EncryptedBankHolderName,
    );
    formData.append(
      "SaveBankDTO.BankName",
      encryptedBankingData.EncryptedBankName,
    );
    formData.append(
      "SaveBankDTO.AccountNumber",
      encryptedBankingData.EncryptedAccountNumber,
    );
    formData.append(
      "SaveBankDTO.RoutingNumber",
      encryptedBankingData.EncryptedRoutingNumber,
    );
    formData.append("SaveBankDTO.AccountType", "C");
    formData.append(
      "SaveBankDTO.Description",
      encryptedBankingData.EncryptedDescription,
    );

    // Employee info
    formData.append("name", data["Employee Name"]);
    formData.append("email", data["Email Address"]);
    formData.append("phone", data["Phone Number"]);
    // Use the numeric city ID, not the display label
    formData.append("cityId", data["CityId"] || data["City"]);
    formData.append("postalCode", data["ZIP Code"]);
    formData.append("addressLine1", data["Address"]);
    formData.append("addressLine2", data["Address"]);
    formData.append(
      "password",
      (data["Employee Name"] || "").trim().split(" ")[0].toLowerCase(),
    );

    // Payroll
    formData.append("payrollCycle", data["Payroll Cycle"] || "");

    // Tax IDs
    const taxIds = formulaList
      .filter((formula) => data[formula.id])
      .map((formula) => formula.id);
    taxIds.forEach((id) => formData.append("taxIds", id));

    dispatch(createEmployee(formData));
  };

  const handleSuccessPopupClose = () => {
    setShowSuccessPopup(false);
    dispatch(resetStatus());
    navigate(-1);
  };

  // ─── Steps config ────────────────────────────────────────────────────────────

  const steps = [
    // STEP 1 — Employee Information
    {
      stepId: "Step1",
      title: "Employee Information",
      subtitle: "",
      showImage: true,
      imageSrc: preview || clint_image_icon,
      fields: [
        {
          label: "Employee Name",
          placeholder: "Enter Employee Name",
          required: true,
        },
        {
          label: "Email Address",
          type: "email",
          placeholder: "Enter Email Address",
          required: true,
        },
        {
          label: "Phone Number",
          type: "tel",
          placeholder: "Enter Phone Number",
          required: true,
          maxLength: 10,
        },
      ],
      imageRequired: false,
      headings: [""],
      showCheckbox: false,
    },
    {
      stepId: "Step2",
      title: "Employee Address",
      subtitle: "",
      showImage: false,
      fields: [
        {
          label: "Country",
          type: "dropdown",
          placeholder: "Select Country",
          required: true,
          options: [
            ...(countries?.map((c) => ({
              value: String(c.id),
              label: c.name,
            })) || []),
            { value: "__add_new__", label: "Add new country" },
          ],
        },
        {
          label: "State",
          type: "dropdown",
          placeholder: "Select State",
          required: true,
          options: [
            ...(states?.map((s) => ({
              value: String(s.id),
              label: s.name,
            })) || []),
            { value: "__add_new__", label: "Add new state" },
          ],
        },
        {
          label: "City",
          type: "dropdown",
          placeholder: "Select City",
          required: true,
          options: [
            ...(cities?.map((c) => ({
              value: String(c.id),
              label: c.name,
            })) || []),
            { value: "__add_new__", label: "Add new city" },
          ],
        },
        {
          label: "ZIP Code",
          type: "text",
          placeholder: "Enter ZIP Code",
          required: true,
        },
        {
          label: "Address",
          type: "text",
          placeholder: "Enter Address",
          required: true,
        },
      ],
      headings: [" "],
      showCheckbox: false,
    },

    // STEP 3 — Job Information
    {
      stepId: "Step3",
      title: "Job Information",
      subtitle: "",
      fields: [
        {
          label: "Designation",
          type: "dropdown",
          placeholder: "Select Designation",
          required: true,
          options: [
            ...(designationList?.map((d) => ({
              value: d.id,
              label: d.name,
            })) || []),
            {
              value: "__add_new__",
              label: "Add new designation",
              isAddNew: true,
            },
          ],
          onChange: (e) => {
            const value = e?.target ? e.target.value : e;
            if (value === "__add_new__") {
              setShowDesignationCreatePopup(true);
              return;
            }
            setSelectedDesignationId(value);
          },
          // ← cross/delete button on each option
          onOptionDelete: (option) => {
            setPendingDeleteId(option?.value ?? null);
            setDeleteField("Designation");
            setShowDeleteConfirm(true);
          },
          defaultValue: selectedDesignationId,
        },
        {
          label: "Department",
          type: "dropdown",
          placeholder: "Select Department",
          required: true,
          options: [
            ...(departmentList?.map((d) => ({
              value: d.id,
              label: d.name,
            })) || []),
            {
              value: "__add_new__",
              label: "Add new department",
              isAddNew: true,
            },
          ],
          onChange: (e) => {
            const value = e?.target ? e.target.value : e;
            if (value === "__add_new__") {
              setSelectedDepartmentId("");
              setShowDepartmentCreatePopup(true);
              return;
            }
            setSelectedDepartmentId(value);
          },
          onOptionDelete: (option) => {
            setPendingDeleteId(option?.value ?? null);
            setDeleteField("Department");
            setShowDeleteConfirm(true);
          },
          defaultValue: selectedDepartmentId,
        },
        {
          label: "Joining Date",
          type: "date",
          required: true,
          placeholder: "Enter Joining Date",
        },
        {
          label: "PayType",
          type: "dropdown",
          placeholder: "Select PayType",
          required: true,
          options: [
            { value: "Hourly", label: "Hourly" },
            { value: "Salary", label: "Salary" },
          ],
          onChange: (e) => setSelectedPayType(e?.target ? e.target.value : e),
        },
        ...(selectedPayType === "Hourly"
          ? [
              {
                label: "Hourly Rate",
                type: "text",
                placeholder: "Enter Hourly Rate",
                required: true,
              },
              {
                label: "Regular Hours",
                type: "text",
                placeholder: "Enter Regular Hours",
                required: true,
              },
            ]
          : []),
        ...(selectedPayType === "Salary"
          ? [
              {
                label: "FixedSalary",
                type: "text",
                placeholder: "Enter Fixed Salary",
                required: true,
              },
            ]
          : []),
      ],
      headings: [" "],
    },

    // STEP 4 — Pay & Tax Setup
    {
      stepId: "Step4",
      title: "Employee Pay & Tax Setup",
      subtitle: "Tax Settings",
      showImage: false,
      fields: [
        {
          label: "Payroll Cycle",
          type: "select",
          placeholder: "Select payroll cycle",
          required: false,
          options: [
            { value: "Monthly", label: "Monthly" },
            { value: "Weekly", label: "Weekly" },
            { value: "BiWeekly", label: "BiWeekly" },
            { value: "SemiMonthly", label: "SemiMonthly" },
          ],
        },
      ],
      showCheckbox: true,
      checkboxes:
        formulaList?.map((item) => ({
          id: item.id,
          label: item.code,
          spanText: `(${item.title || item.name})`,
        })) || [],
    },

    // STEP 5 — Banking Details
    {
      stepId: "Step5",
      title: "Banking Details",
      subtitle: "Final review before submission",
      showImage: false,
      fields: [
        {
          label: "Holder Name",
          placeholder: "Enter Holder Name",
          required: true,
        },
        { label: "Bank Name", placeholder: "Enter Bank Name", required: true },
        {
          label: "Account Number",
          placeholder: "Enter Account Number",
          required: true,
        },
        {
          label: "Routing Number",
          placeholder: "Enter Routing Number",
          required: true,
        },
        {
          label: "Description",
          placeholder: "Enter Description",
          required: true,
          maxLength: 10,
        },
      ],
      maskFields: [
        "Holder Name",
        "Bank Name",
        "Account Number",
        "Routing Number",
        "Description",
      ],
      showCheckbox: false,
      checkboxes: [
        {
          label: "I have reviewed all details",
          spanText: "(Confirm submission)",
        },
      ],
    },
  ];

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="add_new_clint">
        <div className="container-fluid h-100">
          <div className="row h-100">
            <div className="left-content col-md-6 bg_light_grey h-100">
              <div className="content pt_80">
                <h4 className="mb_30">Start Smart. Add Your Next Employee.</h4>
                <p>
                  Quickly create and configure a new employee profile, set
                  payroll preferences — all in a few clicks.
                </p>
              </div>
            </div>

            <div className="col-md-6 bg_white h-100 px-0">
              <input
                type="file"
                accept="image/*"
                id="profileImageInput"
                style={{ display: "none" }}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    setProfileImage(file);
                    setPreview(URL.createObjectURL(file));
                  }
                }}
              />

              <Steps
                steps={steps}
                initialValues={{
                  ...locationFormValues,
                  Designation:
                    location.state?.designationId ||
                    location.state?.selectedDesignationId ||
                    selectedDesignationId ||
                    "",
                  Department:
                    location.state?.departmentId ||
                    location.state?.selectedDepartmentId ||
                    selectedDepartmentId ||
                    "",
                }}
                onSubmit={handleFormSubmit}
                onImageClick={() =>
                  document.getElementById("profileImageInput").click()
                }
                imageSelected={!!profileImage}
                loading={submiting}
                onOpenLocationPopup={handleOpenLocationPopup}
              />
            </div>
          </div>
        </div>
      </div>

      {/* ── Delete confirm modal ─────────────────────────────────────────────── */}
      <Modal
        show={showDeleteConfirm}
        onHide={() => setShowDeleteConfirm(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this {deleteField}?
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowDeleteConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => {
              if (deleteField === "Department") {
                dispatch(deleteDepartment(pendingDeleteId)).then(() => {
                  dispatch(fetchAllDepartments()); // ← refresh after delete
                });
              }
              if (deleteField === "Designation") {
                dispatch(deleteDesignation(pendingDeleteId)).then(() => {
                  dispatch(fetchAllDesignations()); // ← refresh after delete
                });
              }
              setShowDeleteConfirm(false);
            }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Create popups ────────────────────────────────────────────────────── */}
      <DepartmentCreatePopup
        show={showDepartmentCreatePopup}
        onHide={() => setShowDepartmentCreatePopup(false)}
        onSuccess={(dept) => {
          dispatch(fetchAllDepartments());
          setSelectedDepartmentId(String(dept.id));
        }}
      />

      <DesignationCreatePopup
        show={showDesignationCreatePopup}
        onHide={() => setShowDesignationCreatePopup(false)}
        onSuccess={(desig) => {
          dispatch(fetchAllDesignations());
          setSelectedDesignationId(String(desig.id));
        }}
      />

      <CountryCreatePopup
        show={showCountryPopup}
        onHide={() => setShowCountryPopup(false)}
        onSuccess={handleCountryCreated}
      />

      <StateCreatePopup
        show={showStatePopup}
        onHide={() => setShowStatePopup(false)}
        onSuccess={handleStateCreated}
        selectedCountryId={selectedCountryId}
      />

      <CityCreatePopup
        show={showCityPopup}
        onHide={() => setShowCityPopup(false)}
        onSuccess={handleCityCreated}
        selectedStateId={selectedStateId}
      />

      {/* ── Status popups ────────────────────────────────────────────────────── */}
      <StatusPopup
        show={errorMessage !== null}
        type="error"
        title="Error"
        message={errorMessage}
        handleClose={() => {
          dispatch(resetStatus());
          setSubmiting(false);
        }}
      />

      <EditSuccessPopup
        show={showSuccessPopup}
        onHide={handleSuccessPopupClose}
        entityType="employee"
        operationType="add"
        title="Employee Created Successfully"
      />
    </>
  );
};

export default AddNewEmployee;
